/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, HStack, useToast, VStack } from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import { FaCalendarCheck, FaRegCalendarCheck } from 'react-icons/fa';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Text } from '../../../../components/atoms/Text';
import { Button } from '../../../../components/atoms/Button';
import { useTheme } from '../../../../contexts/ThemeProvider';
import { masks } from '../../../../utils/masks';
import { bookAppointment, BookingDraft } from '../../../../service/telemedicine/api';
import { storeAppointment } from '../../hooks/useOpenAppointment';
import { parseDateKey } from '../../utils/date';
import { getErrorMessage } from '../../utils/errors';

interface CompletionStepProps {
  draft: BookingDraft;
  planName?: string;
  /** Rechecagem da consulta em aberto — a página bloqueia a tela quando encontra uma. */
  onOpenAppointmentDetected: () => void;
  /** Chamado quando o agendamento é confirmado com sucesso. */
  onConfirmed?: () => void;
}

const TOAST_ERROR = (message: string) => ({
  title: message,
  status: 'error' as const,
  duration: 9000,
  isClosable: true,
});

function SummaryRow({ label, value }: { label: string; value?: string }) {
  if (!value) return null;

  return (
    <HStack justify={'space-between'} align={'flex-start'} spacing={3} width={'100%'}>
      <Text fontSize={'13px'} color={'#4A5568'}>
        {label}
      </Text>
      <Text fontSize={'13px'} fontWeight={600} textAlign={'right'} wordBreak={'break-word'}>
        {value}
      </Text>
    </HStack>
  );
}

export function CompletionStep({ draft, planName, onOpenAppointmentDetected, onConfirmed }: CompletionStepProps) {
  const { colors } = useTheme();
  const toast = useToast();

  const [isBooking, setIsBooking] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleConfirm = useCallback(async () => {
    setIsBooking(true);
    try {
      const result = await bookAppointment({
        patient: {
          document: masks.unmask(draft.cpf),
          full_name: draft.name,
          birth_date: draft.birthDate,
          phone: masks.unmask(draft.phone),
          email: draft.email,
        },
        slot: {
          date: draft.date,
          time: draft.slot.time,
        },
      });

      storeAppointment({
        id: result.appointment_id,
        status: result.status,
        date: draft.date,
        time: draft.slot.time,
        patient_name: draft.name,
      });

      setIsConfirmed(true);
      onConfirmed?.();
    } catch (error: any) {
      // 409 aqui normalmente é corrida: outra aba/dispositivo agendou entre a checagem e
      // a confirmação — reconfere o estado em vez de só mostrar um erro genérico.
      if (error?.response?.status === 409) {
        onOpenAppointmentDetected();
        return;
      }
      toast(TOAST_ERROR(getErrorMessage(error, 'Não foi possível confirmar o agendamento agora. Tente novamente em instantes.')));
    } finally {
      setIsBooking(false);
    }
  }, [draft, onConfirmed, onOpenAppointmentDetected, toast]);

  // Mesmo resumo completo nos dois momentos: antes de confirmar (pra revisão) e depois de
  // enviado. O médico nunca aparece — o atendimento é da clínica.
  const summaryRows = (
    <VStack align={'stretch'} spacing={2}>
      <SummaryRow label="Paciente" value={draft.name} />
      <SummaryRow label="CPF" value={draft.cpf} />
      <SummaryRow label="Plano" value={planName} />
      <SummaryRow label="E-mail" value={draft.email} />
      <SummaryRow label="WhatsApp" value={draft.phone} />
      <SummaryRow label="Especialidade" value={'Clínico Geral'} />
      <SummaryRow label="Data" value={format(parseDateKey(draft.date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })} />
      <SummaryRow label="Horário" value={draft.slot.time} />
    </VStack>
  );

  if (!isConfirmed) {
    return (
      <VStack align={'center'} spacing={4} width={'100%'} paddingY={6} textAlign={'center'}>
        <Box
          width={'64px'}
          height={'64px'}
          borderRadius={'full'}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          backgroundColor={'#F7F9FA'}
          color={colors.primaryColor}
        >
          <FaRegCalendarCheck size={'28px'} />
        </Box>

        <Text fontSize={'20px'} fontWeight={700}>
          Confirme seu agendamento
        </Text>

        <Text fontSize={'14px'} color={'#4A5568'} maxWidth={'420px'}>
          Revise os dados abaixo. Ao confirmar, o horário é reservado e a confirmação chega
          {draft.email ? ` em ${draft.email}` : ' no seu e-mail'}.
        </Text>

        <Box width={'100%'} maxWidth={'420px'} padding={'16px'} borderRadius={'14px'} backgroundColor={'#F7F9FA'} border={'1px solid #E2E8F0'} textAlign={'left'}>
          <Text fontSize={'13px'} fontWeight={700} color={'#4A5568'} marginBottom={2}>
            Resumo do agendamento
          </Text>
          {summaryRows}
        </Box>

        <Box width={'100%'} maxWidth={'420px'}>
          <Button isLoading={isBooking} onClick={handleConfirm}>
            Confirmar agendamento
          </Button>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack align={'center'} spacing={4} width={'100%'} paddingY={6} textAlign={'center'}>
      <Box
        width={'64px'}
        height={'64px'}
        borderRadius={'full'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        backgroundColor={'#F0FFF4'}
        color={'#22543D'}
      >
        <FaCalendarCheck size={'28px'} />
      </Box>

      <Text fontSize={'20px'} fontWeight={700}>
        Agendamento enviado com sucesso!
      </Text>

      <Text fontSize={'14px'} color={'#4A5568'} maxWidth={'420px'}>
        Tudo certo, {draft.name}. Você vai receber a confirmação do agendamento em {draft.email}. Perto do horário
        marcado, o link para entrar na sala também será enviado para o seu e-mail — é só clicar nele na hora do
        atendimento.
      </Text>

      <Box width={'100%'} maxWidth={'420px'} padding={'16px'} borderRadius={'14px'} backgroundColor={'#F7F9FA'} border={'1px solid #E2E8F0'} textAlign={'left'}>
        <Text fontSize={'13px'} fontWeight={700} color={'#4A5568'} marginBottom={2}>
          Resumo do agendamento
        </Text>
        {summaryRows}
      </Box>

      <Box width={'100%'} maxWidth={'420px'} padding={'14px'} borderRadius={'12px'} backgroundColor={'#F7F9FA'} borderLeft={'3px solid'} borderColor={colors.primaryColor}>
        <Text fontSize={'13px'} fontWeight={600} color={'#4A5568'} marginBottom={1}>
          Como entrar na sala
        </Text>
        <Text fontSize={'13px'} color={'#4A5568'}>
          O link de acesso ao atendimento chega no seu e-mail perto do horário marcado — não é preciso guardar nada
          agora, é só abrir o e-mail na hora.
        </Text>
      </Box>

      <Box width={'100%'} maxWidth={'420px'} padding={'14px'} borderRadius={'12px'} backgroundColor={'#F7F9FA'} borderLeft={'3px solid'} borderColor={colors.primaryColor}>
        <Text fontSize={'13px'} color={'#4A5568'}>
          Chegue com 5 minutos de antecedência, em local com boa conexão de internet, e tenha o documento validado em
          mãos caso o médico solicite conferência durante o atendimento.
        </Text>
      </Box>
    </VStack>
  );
}
